import React, { useState, useEffect, useCallback } from "react";
import { debounce } from "lodash";
import Header from "../components/HeaderComponent";
import TablePagination from "../components/TablePagination";
import { config } from '../config.js'

// Import Images
import searchIcon from '../assets/images/icons/search-gray-icon.svg';
import arrowDownIcon from '../assets/images/icons/arrow-angle-down-small-icon.svg';
import transactionInIcon from "../assets/images/icons/transaction-in-arrow.svg";
import transactionOutIcon from "../assets/images/icons/transaction-out-arrow.svg";
import whiteLogo from "../assets/images/logo-white.svg";

const contentlimit = 20;

function OtcTrade() {
    const [isLoading, setIsLoading] = useState(false);
    const [transectionList, setTransectionList] = useState([]);
    const [transectionCount, setTransectionCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [cache, setCache] = useState({});

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const fetchTransactions = useCallback(async (skip = 0, limit = contentlimit, query = null) => {
        const cacheKey = `${skip}-${limit}-${query || 'default'}`;

        // Return cached data if available
        if (cache[cacheKey]) {
            setTransectionList(cache[cacheKey].details);
            setTransectionCount(cache[cacheKey].count);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const sessionvalue = JSON.parse(sessionStorage.getItem("logedinUser"));
            if (!sessionvalue?.email) {
                setError("User session not found");
                return;
            }

            const body = query
                ? `{ "query": ${query}, "email": "${sessionvalue.email}", "skip":${skip}, "limit":${limit} }`
                : `{ "email": "${sessionvalue.email}", "skip":${skip}, "limit":${limit} }`;

            const response = await fetch(config.API_BASE_URL + '/getntransectionhistory', {
                method: "POST",
                body: body
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data?.data) {
                setTransectionList(data.data.details || []);
                setTransectionCount(data.data.transectioncount || 0);

                // Update cache
                setCache(prev => ({
                    ...prev,
                    [cacheKey]: {
                        details: data.data.details,
                        count: data.data.transectioncount
                    }
                }));
            } else {
                setError("Invalid data format received");
            }
        } catch (err) {
            setError("Failed to load transactions. Please try again.");
            setTransectionList([]);
            setTransectionCount(0);
        } finally {
            setIsLoading(false);
        }
    }, [cache]);

    // Debounced search handler
    const debouncedSearch = useCallback(debounce((query) => {
        setCurrentPage(1); // Reset to first page on new search
        if (query.length >= 2) {
            const searchQueryString = `{
                "$or": [
                    { "fromaddress": { "$regex": "${query}", "$options": "i" } },
                    { "toaddress": { "$regex": "${query}", "$options": "i" } }
                ]
            }`;
            fetchTransactions(0, contentlimit, searchQueryString);
        } else {
            fetchTransactions();
        }
    }, 500), [fetchTransactions]);

    // Handle search input changes
    const handleSearchChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);
        debouncedSearch(query);
    };

    // Handle page changes
    const handlePageChange = (page) => {
        setCurrentPage(page);
        const skip = (page - 1) * contentlimit;

        if (searchQuery.length >= 2) {
            const searchQueryString = `{
                "$or": [
                    { "fromaddress": { "$regex": "${searchQuery}", "$options": "i" } },
                    { "toaddress": { "$regex": "${searchQuery}", "$options": "i" } }
                ]
            }`;
            fetchTransactions(skip, contentlimit, searchQueryString);
        } else {
            fetchTransactions(skip, contentlimit);
        }
    };

    // Download file handler
    const downloadFile = async (filename) => {
        try {
            if (!filename || ['undefined', 'null', 'false'].includes(filename)) {
                alert("File not found or invalid filename.");
                return;
            }

            // Open in new tab (simplest approach if server sets proper headers)
            window.open(config.API_BASE_URL + '/download/' + filename, '_blank');
        } catch (error) {
            alert(`Download failed: ${error.message}`);
        }
    };

    // Initial load
    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    return (
        <div className='home-page'>
            <Header />
            <section className="sec-banner">
                <div className="container-fluid">
                    <div className="banner-wrapper">
                        <div className="top-notch-wrapper">
                            <span className="top-notch"></span>
                            <a href="#" className="logo">
                                <img src={whiteLogo} alt="logo" />
                            </a>
                        </div>
                        <div className="container">
                            <div className="otc-banner-wrapper">
                                <div className="row">
                                    <div className="col-md-6">
                                        <h2 className="w-500">Transaction History</h2>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section className="sec-wallet sec-otc-wallet">
                <div className="container">
                    <div className="wallet-wrapper mt-375">
                        <div className="wallet-wrapper-table">
                            <div className="asset-wrapper">
                                <div className="asset-header">
                                    <h3 className="f-30 w-400 h-font title">Transaction History</h3>
                                    <div className="row">
                                        <div className="col-lg-6 col-md-4 align-self-center">
                                            <div className="search-wrapper-v1 w-100 mb-16">
                                                <div className="form">
                                                    <div className="form-group">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Search"
                                                            value={searchQuery}
                                                            onChange={handleSearchChange}
                                                        />
                                                        <img src={searchIcon} className="icon" alt="icon" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="asset-body">
                                    {isLoading ? (
                                        <div className="loading-indicator">Loading transactions...</div>
                                    ) : error ? (
                                        <div className="error-message text-danger">{error}</div>
                                    ) : (
                                        <div className="asset-list-wrapper">
                                            <div className="asset-list desktop">
                                                <div className="table-header style-v2 grid-8">
                                                    <div className="asset-item p-0"></div>
                                                    <div className="asset-item">
                                                        <p></p>
                                                    </div>
                                                    <div className="asset-item">
                                                        <p>Transaction  Hash</p>
                                                    </div>
                                                    <div className="asset-item">
                                                        <p>Type <img src={arrowDownIcon} alt="icon" /></p>
                                                    </div>
                                                    <div className="asset-item">
                                                        <p>Amount</p>
                                                    </div>
                                                    <div className="asset-item">
                                                        <p>Status</p>
                                                    </div>
                                                    <div className="asset-item">
                                                        <p>Date</p>
                                                    </div>
                                                    <div className="asset-item">
                                                        <p>Recipt</p>
                                                    </div>
                                                </div>
                                                {transectionList.length > 0 ? (
                                                    transectionList.map((transaction, index) => (
                                                        <div key={index} className="asset-card grid-8">
                                                            <div className="asset-item"></div>
                                                            <div className="asset-item">
                                                                <img
                                                                    src={transaction.transactiontype === 'CREDITED' ? transactionInIcon : transactionOutIcon}
                                                                    alt="transaction type"
                                                                />
                                                            </div>
                                                            <div className="asset-item">
                                                                <a
                                                                    href={`https://testnet.xdcscan.com/tx/${transaction.transectionhash}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="p w-500 text-break p-h-1"
                                                                    title={transaction.transectionhash}
                                                                >
                                                                    {transaction.transectionhash}
                                                                </a>
                                                            </div>
                                                            <div className="asset-item">
                                                                <p className="p w-500">{transaction.transactiontype}</p>
                                                            </div>
                                                            <div className="asset-item">
                                                                <p title={transaction.priceamount + " " + transaction.currencycodeૈૈૈૈૈૈૈૈૈૈૈૈૈૈૈૈૈૈૈૈૈૈ} className="p w-500 p-a-1">{transaction.priceamount} {transaction.currencycode}</p>
                                                            </div>
                                                            <div className="asset-item">
                                                                <p className="p w-500">{transaction.status}</p>
                                                            </div>
                                                            <div className="asset-item">
                                                                <p className="p w-500">{formatDate(transaction.createdAt)}</p>
                                                            </div>
                                                            <div className="asset-item">
                                                                <p className="p w-500 user-select-auto" onClick={() => downloadFile(transaction.filename)}>{transaction.filename ? transaction.filename : '-'}</p>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="no-results">No transactions found</div>
                                                )}
                                            </div>
                                            {/* Mobile View */}
                                            <div className="asset-list-mobile">
                                                {transectionList.length > 0 ? (
                                                    transectionList.map((transaction, index) => (
                                                        <div key={index} className="asset-list-card">
                                                            <div className="transaction-icon">
                                                                <img
                                                                    src={transaction.transactiontype === 'CREDITED' ? transactionInIcon : transactionOutIcon}
                                                                    alt="transaction type"
                                                                />
                                                            </div>
                                                            <ul className="trade-date-list">
                                                                <li>
                                                                    <p className="f-10">Date</p>
                                                                    <p className="p w-600">{formatDate(transaction.createdAt)}</p>
                                                                </li>
                                                                <li>
                                                                    <p className="f-10">Status</p>
                                                                    <p className="p w-600">{transaction.status}</p>
                                                                </li>
                                                            </ul>
                                                            <ul className="source-date-list">
                                                                <li>
                                                                    <p className="f-10">Source</p>
                                                                    <p className="p w-600 wallet-name">{transaction.fromaddress}</p>
                                                                </li>
                                                                <li>
                                                                    <p className="f-10">Destination</p>
                                                                    <p className="p w-600">{transaction.toaddress}</p>
                                                                </li>
                                                            </ul>
                                                            <ul className="destination-date-list">
                                                                <li>
                                                                    <p className="f-10">Currency</p>
                                                                    <p className="p w-600">{transaction.currencyname}</p>
                                                                </li>
                                                                <li>
                                                                    <p className="f-10">Amount</p>
                                                                    <p className="p w-600">{transaction.priceamount} {transaction.currencycode}</p>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="no-results">No transactions found</div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pagination Component */}
            {!isLoading && !error && transectionCount > contentlimit && (
                <div className="pagination-wrapper">
                    <TablePagination
                        totalData={transectionCount}
                        pageLimit={contentlimit}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}
        </div>
    );
}

export default OtcTrade;